import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { EntityIssuesType } from 'hooks/useEntityIssues';
import type { ITestSession } from 'api/concierge';

export interface IConciergeIssuesTarget {
    type: EntityIssuesType;
    id: number;
}

export interface ITool {
    name: string;
    icon: string | {};
    title: string;
}

export interface ICustomService {
    name: string;
    domain_id?: string;
    authInfo?: {
        authUrl: string;
        remainDays: number;
        providerName: string;
        tokenFieldName: string;
    };
}

export interface IDomain {
    concierge_domain_id: string;
    name: string;
    icon_ind: string;
    icon_name: string;
    icon_data: string;
    title: string;
    description?: string;
    suggestedPrompts?: string[];
    customServices?: ICustomService[];
    reportDataState?: 'no_access' | 'enabled';
    additionalResources?: boolean;
}

export interface MappedDomains {
    [key: string]: string[];
}

export interface IConciergeCustomEvents {
    owner_click?: boolean;
    certified_username_click?: boolean;
}

export interface conciergeSliceState {
    visible: boolean;
    closeChat: boolean;
    onlySearch: boolean;
    isAppPanel: boolean;
    isMobile: boolean;
    fromSearch: boolean;
    width: number;
    newChat: string | null;
    availableDomains: IDomain[];
    availableTools: ITool[];
    selectedTools: string[];
    selectedDomains: string[];
    defaultSelectedDomains: string[] | null | undefined;
    focusInput: boolean;
    customStyles: any;
    customEvents: IConciergeCustomEvents;
    messageDomains: { [messageUid: string]: string };
    authorizedTools: string[];
    newRequestMessage: string | null;
    prependContext: { text: string; showInChat: boolean } | null;
    issuesTarget: IConciergeIssuesTarget | null;
    targetQuestionId: string | null;
    elementConciergeVisibility: 'visible' | 'hidden' | null;
    testSession: ITestSession | null;
}

export const conciergeSlice = createSlice({
    name: 'concierge',
    initialState: {
        visible: false,
        closeChat: false,
        width: 420,
        newChat: null,
        onlySearch: false,
        fromSearch: false,
        availableTools: [],
        availableDomains: [],
        selectedTools: [],
        selectedDomains: [],
        defaultSelectedDomains: null,
        focusInput: false,
        customStyles: {},
        customEvents: {},
        isAppPanel: false,
        isMobile: false,
        messageDomains: {},
        authorizedTools: [],
        newRequestMessage: null,
        prependContext: null,
        issuesTarget: null,
        targetQuestionId: null,
        elementConciergeVisibility: null,
        testSession: null,
    } as conciergeSliceState,
    reducers: {
        show: (state) => {
            state.visible = true;
        },
        hide: (state) => {
            state.visible = false;
        },
        toggleVisibility: (state) => {
            state.visible = !state.visible;
        },
        setConciergeWidgetWidth: (state, action) => {
            state.width = action.payload;
        },
        startNewChat: (state, action) => {
            state.newChat = action.payload;
        },
        clearNewChat: (state) => {
            state.newChat = null;
        },
        sendNewRequestMessage: (state, action) => {
            state.newRequestMessage = action.payload;
        },
        clearNewRequestMessage: (state) => {
            state.newRequestMessage = null;
        },
        closeChat: (state, action) => {
            state.closeChat = action.payload;
        },
        setOnlySearch: (state, action) => {
            state.onlySearch = action.payload;
        },
        setAvailableDomains: (state, action) => {
            state.availableDomains = action.payload;
        },
        setAvailableTools: (state, action) => {
            state.availableTools = action.payload;
        },
        setSelectedDomains: (state, action) => {
            state.selectedDomains = action.payload;
            state.focusInput = !state.focusInput;
        },
        setFromSearch: (state, action) => {
            state.fromSearch = action.payload;
        },
        setDefaultSelectedDomains: (state, action) => {
            state.defaultSelectedDomains = action.payload;
        },
        setCustomStyles: (state, action) => {
            state.customStyles = action.payload;
        },
        setIsAppPanel: (state, action) => {
            state.isAppPanel = action.payload;
        },
        setConciergeCustomEvents: (state, action: PayloadAction<IConciergeCustomEvents>) => {
            state.customEvents = action.payload;
        },
        setIsMobile: (state, action) => {
            state.isMobile = action.payload;
        },
        setMessageDomain: (state, action) => {
            const { messageUid, domainName } = action.payload;
            state.messageDomains[messageUid] = domainName;
        },
        addAuthorizedTool: (state, action) => {
            const toolName = action.payload;
            if (!state.authorizedTools.includes(toolName)) {
                state.authorizedTools.push(toolName);
            }
        },
        setPrependContext: (state, action) => {
            state.prependContext = action.payload;
        },
        clearPrependContext: (state) => {
            state.prependContext = null;
        },
        setIssuesTarget: (state, action: PayloadAction<IConciergeIssuesTarget | null>) => {
            state.issuesTarget = action.payload;
        },
        setElementConciergeVisibility: (state, action: PayloadAction<'visible' | 'hidden' | null>) => {
            state.elementConciergeVisibility = action.payload;
        },
        setTargetQuestionId: (state, action: PayloadAction<string | null>) => {
            state.targetQuestionId = action.payload;
        },
        clearTargetQuestionId: (state) => {
            state.targetQuestionId = null;
        },
        setTestSession: (state, action: PayloadAction<ITestSession>) => {
            state.testSession = action.payload;
        },
        clearTestSession: (state) => {
            state.testSession = null;
        },
    },
});

export const {
    show,
    hide,
    toggleVisibility,
    setConciergeWidgetWidth,
    startNewChat,
    clearNewChat,
    closeChat,
    setOnlySearch,
    setAvailableDomains,
    setSelectedDomains,
    setAvailableTools,
    setFromSearch,
    setDefaultSelectedDomains,
    setCustomStyles,
    setIsAppPanel,
    setIsMobile,
    setMessageDomain,
    addAuthorizedTool,
    sendNewRequestMessage,
    clearNewRequestMessage,
    setPrependContext,
    clearPrependContext,
    setConciergeCustomEvents,
    setIssuesTarget,
    setElementConciergeVisibility,
    setTargetQuestionId,
    clearTargetQuestionId,
    setTestSession,
    clearTestSession,
} = conciergeSlice.actions;

export default conciergeSlice.reducer;
